import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import {
  buildApiMessages,
  compressContext,
  shouldCompress,
} from "../contextCompress";
import { bindCopyButtons, renderMarkdown } from "../markdown";
import { SelectMenu } from "../SelectMenu";
import {
  createSession,
  exportSession,
  loadSessions,
  saveSessions,
  sessionTitle,
  type Session,
} from "../sessions";

const DEFAULT_MODEL = "qwen36-local:latest";

const CTX_OPTIONS = [
  { value: "8192", label: "CTX 8k" },
  { value: "16384", label: "CTX 16k" },
  { value: "32768", label: "CTX 32k" },
  { value: "65536", label: "CTX 64k" },
  { value: "131072", label: "CTX 128k" },
];

const PREDICT_OPTIONS = [
  { value: "2048", label: "MAX 2k" },
  { value: "4096", label: "MAX 4k" },
  { value: "8192", label: "MAX 8k" },
  { value: "16384", label: "MAX 16k" },
  { value: "32768", label: "MAX 32k" },
];

const PROMPTS = [
  { title: "Объясни код", hint: "Коротко и по делу", text: "Объясни этот код коротко" },
  { title: "Найди баг", hint: "И предложи фикс", text: "Найди баг и предложи фикс" },
  { title: "Сравни подходы", hint: "Плюсы и минусы", text: "Сравни два подхода" },
];

function formatTokens(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `${k >= 10 ? Math.round(k) : Math.round(k * 10) / 10}k`;
  }
  return String(n);
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "сейчас";
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} д`;
  return new Date(ts).toLocaleDateString([], { month: "short", day: "numeric" });
}

export function ChatPage() {
  const { logout, sub } = useAuth();
  const [sessions, setSessions] = useState<Session[]>(() => {
    const existing = loadSessions();
    return existing.length ? existing : [createSession()];
  });
  const [activeId, setActiveId] = useState(() => sessions[0]?.id);
  const [models, setModels] = useState<string[]>([]);
  const [model, setModel] = useState("");
  const [think, setThink] = useState(false);
  const [numPredict, setNumPredict] = useState(8192);
  const [numCtx, setNumCtx] = useState(32768);
  const [ctxUsed, setCtxUsed] = useState<number | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [health, setHealth] = useState<"unknown" | "ok" | "bad">("unknown");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth > 900 : true
  );
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mdRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const active = useMemo(
    () => sessions.find((s) => s.id === activeId) || sessions[0],
    [sessions, activeId]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => s.title.toLowerCase().includes(q));
  }, [sessions, query]);

  useEffect(() => {
    saveSessions(sessions);
  }, [sessions]);

  useEffect(() => {
    void (async () => {
      try {
        const h = await api.health();
        setHealth(h.ok ? "ok" : "bad");
      } catch {
        setHealth("bad");
      }
      try {
        const { models: list } = await api.models();
        const names = list.map((m) => m.name);
        setModels(names);
        setModel((prev) => {
          if (prev && names.includes(prev)) return prev;
          if (names.includes(DEFAULT_MODEL)) return DEFAULT_MODEL;
          return names[0] || "";
        });
      } catch {
        setModels([]);
      }
    })();
  }, []);

  useEffect(() => {
    bindCopyButtons(mdRef.current);
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [active?.messages, busy]);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(180, Math.max(48, ta.scrollHeight))}px`;
  }, [input]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    function onResize() {
      if (window.innerWidth > 900) setSideOpen(true);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function patchActive(mut: (s: Session) => Session) {
    setSessions((prev) =>
      prev.map((s) => (s.id === active?.id ? mut({ ...s, updatedAt: Date.now() }) : s))
    );
  }

  function newChat() {
    const s = createSession(model || undefined);
    setSessions((prev) => [s, ...prev]);
    setActiveId(s.id);
    setQuery("");
    setCtxUsed(null);
    setTruncated(false);
    if (window.innerWidth <= 900) setSideOpen(false);
    requestAnimationFrame(() => taRef.current?.focus());
  }

  function selectSession(id: string) {
    setActiveId(id);
    setMenuOpen(false);
    setCtxUsed(null);
    const s = sessions.find((x) => x.id === id);
    const last = s?.messages[s.messages.length - 1];
    setTruncated(Boolean(last?.role === "assistant" && last.truncated));
    if (window.innerWidth <= 900) setSideOpen(false);
  }

  function deleteChat(id?: string) {
    const target = id || active?.id;
    if (!target) return;
    setMenuOpen(false);
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== target);
      if (!next.length) {
        const s = createSession(model || undefined);
        setActiveId(s.id);
        return [s];
      }
      if (activeId === target) setActiveId(next[0].id);
      return next;
    });
  }

  function clearMessages() {
    setMenuOpen(false);
    setCtxUsed(null);
    setTruncated(false);
    patchActive((s) => ({
      ...s,
      messages: [],
      title: "New chat",
      summary: undefined,
      summarizedCount: 0,
    }));
  }

  function doExport() {
    if (!active) return;
    setMenuOpen(false);
    const blob = new Blob([exportSession(active)], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active.title.replace(/[^\w.-]+/g, "_") || "chat"}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  }

  async function ensureCompressedPayload(
    history: { role: "user" | "assistant"; content: string }[],
    signal: AbortSignal
  ): Promise<{ role: "system" | "user" | "assistant"; content: string }[]> {
    let summary = active?.summary;
    let summarizedCount = active?.summarizedCount || 0;
    const asMsg = history;

    const need = shouldCompress({
      history: asMsg,
      summary,
      summarizedCount,
      numCtx,
      ctxUsed,
      reservePredict: numPredict,
    });

    if (need) {
      setCompressing(true);
      try {
        const result = await compressContext({
          model,
          history: asMsg,
          summary,
          summarizedCount,
          numCtx,
          signal,
        });
        summary = result.summary;
        summarizedCount = result.summarizedCount;
        patchActive((s) => ({
          ...s,
          summary: result.summary,
          summarizedCount: result.summarizedCount,
        }));
        setCtxUsed(null);
      } finally {
        setCompressing(false);
      }
    }

    return buildApiMessages(asMsg, summary, summarizedCount);
  }

  async function runGeneration(opts: {
    history: { role: "user" | "assistant"; content: string }[];
    /** Append deltas into existing last assistant bubble */
    appendToLastAssistant?: boolean;
  }) {
    if (!model) {
      setErr("Нет модели — проверь Ollama");
      return;
    }
    setErr(null);
    setTruncated(false);
    setBusy(true);
    const ac = new AbortController();
    abortRef.current = ac;

    if (!opts.appendToLastAssistant) {
      patchActive((s) => ({
        ...s,
        messages: [
          ...opts.history,
          { role: "assistant" as const, content: "", truncated: false },
        ],
        title: sessionTitle(opts.history),
        model,
      }));
    } else {
      patchActive((s) => {
        const msgs = [...s.messages];
        const last = msgs[msgs.length - 1];
        if (last?.role === "assistant") {
          msgs[msgs.length - 1] = { ...last, truncated: false };
        }
        return { ...s, messages: msgs, model };
      });
    }

    try {
      const apiMessages = await ensureCompressedPayload(opts.history, ac.signal);
      await api.chatStream(
        {
          model,
          messages: apiMessages,
          think: think,
          options: { num_predict: numPredict, num_ctx: numCtx },
        },
        {
          signal: ac.signal,
          onDelta: ({ content, thinking }) => {
            patchActive((s) => {
              const msgs = [...s.messages];
              const last = msgs[msgs.length - 1];
              if (!last || last.role !== "assistant") return s;
              msgs[msgs.length - 1] = {
                ...last,
                content: last.content + (content || ""),
                ...(think ? { thinking: (last.thinking || "") + (thinking || "") } : {}),
              };
              return { ...s, messages: msgs };
            });
          },
          onDone: (info) => {
            setTruncated(info.truncated);
            if (typeof info.context_used === "number" && info.context_used > 0) {
              setCtxUsed(info.context_used);
            }
            if (info.truncated) {
              patchActive((s) => {
                const msgs = [...s.messages];
                const last = msgs[msgs.length - 1];
                if (last?.role === "assistant") {
                  msgs[msgs.length - 1] = { ...last, truncated: true };
                }
                return { ...s, messages: msgs };
              });
            }
          },
          onError: (msg) => setErr(msg),
        }
      );
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        const msg = e instanceof Error ? e.message : "chat failed";
        setErr(msg);
        if (!opts.appendToLastAssistant) {
          patchActive((s) => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === "assistant" && !last.content) msgs.pop();
            return { ...s, messages: msgs };
          });
        }
      }
    } finally {
      setBusy(false);
      setCompressing(false);
      abortRef.current = null;
      taRef.current?.focus();
    }
  }

  async function send(textOverride?: string) {
    const text = (textOverride ?? input).trim();
    if (!text || !active || busy) return;
    setInput("");
    const userMsg = { role: "user" as const, content: text };
    const history = [...active.messages, userMsg];
    await runGeneration({ history });
  }

  /** Continue a reply cut by num_predict / context */
  async function continueReply() {
    if (!active || busy) return;
    const msgs = active.messages;
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== "assistant" || !last.content) return;

    const history = [
      ...msgs.map((m) => ({ role: m.role, content: m.content })),
      {
        role: "user" as const,
        content:
          "Continue the previous answer exactly from where it stopped. Do not restart or repeat earlier text — only continue.",
      },
    ];

    setBusy(true);
    setErr(null);
    setTruncated(false);
    const ac = new AbortController();
    abortRef.current = ac;
    patchActive((s) => {
      const next = [...s.messages];
      const a = next[next.length - 1];
      if (a?.role === "assistant") next[next.length - 1] = { ...a, truncated: false };
      return { ...s, messages: next };
    });

    try {
      const apiMessages = await ensureCompressedPayload(history, ac.signal);
      await api.chatStream(
        {
          model,
          messages: apiMessages,
          think: think,
          options: { num_predict: numPredict, num_ctx: numCtx },
        },
        {
          signal: ac.signal,
          onDelta: ({ content, thinking }) => {
            patchActive((s) => {
              const list = [...s.messages];
              const a = list[list.length - 1];
              if (!a || a.role !== "assistant") return s;
              list[list.length - 1] = {
                ...a,
                content: a.content + (content || ""),
                ...(think ? { thinking: (a.thinking || "") + (thinking || "") } : {}),
              };
              return { ...s, messages: list };
            });
          },
          onDone: (info) => {
            setTruncated(info.truncated);
            if (typeof info.context_used === "number" && info.context_used > 0) {
              setCtxUsed(info.context_used);
            }
            if (info.truncated) {
              patchActive((s) => {
                const list = [...s.messages];
                const a = list[list.length - 1];
                if (a?.role === "assistant") list[list.length - 1] = { ...a, truncated: true };
                return { ...s, messages: list };
              });
            }
          },
          onError: (msg) => setErr(msg),
        }
      );
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        setErr(e instanceof Error ? e.message : "continue failed");
      }
    } finally {
      setBusy(false);
      setCompressing(false);
      abortRef.current = null;
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  const empty = !active?.messages.length;

  return (
    <div className={`shell chat-app${sideOpen ? "" : " side-collapsed"}`}>
      <div className="noise" aria-hidden />
      <div className="aurora aurora-chat" aria-hidden />

      <button
        type="button"
        className="side-scrim"
        aria-label="Закрыть меню"
        tabIndex={sideOpen ? 0 : -1}
        onClick={() => setSideOpen(false)}
      />

      <aside className="chat-side">
        <div className="side-head">
          <Link className="brand side-brand" to="/">
            Backup<span className="slash">Node</span>
            <small>private llm</small>
          </Link>
          <button type="button" className="btn primary side-new" onClick={newChat}>
            <span className="ico-plus" aria-hidden />
            Новый чат
          </button>
          <label className="side-search">
            <span className="visually-hidden">Поиск сессий</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск…"
              type="search"
            />
          </label>
        </div>

        <div className="side-label">Чаты</div>
        <div className="side-list">
          {filtered.length === 0 ? (
            <p className="side-empty">Ничего не найдено</p>
          ) : (
            filtered.map((s) => (
              <div
                key={s.id}
                className={`side-item${s.id === active?.id ? " active" : ""}`}
              >
                <button
                  type="button"
                  className="side-item-main"
                  onClick={() => selectSession(s.id)}
                >
                  <span className="t">{s.title}</span>
                  <span className="d">{relativeTime(s.updatedAt)}</span>
                </button>
                <button
                  type="button"
                  className="side-item-del"
                  title="Удалить"
                  aria-label={`Удалить ${s.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteChat(s.id);
                  }}
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>

        <div className="side-foot">
          <div className="side-user">
            <span className="side-avatar" aria-hidden>
              {(sub || "u").slice(0, 1).toUpperCase()}
            </span>
            <span className="side-user-meta">
              <span className="side-user-name">{sub || "user"}</span>
              <span className={`side-health ${health}`}>
                {health === "ok" ? "Ollama online" : health === "bad" ? "Ollama offline" : "…"}
              </span>
            </span>
          </div>
          <button type="button" className="btn sm ghost" onClick={() => void logout()}>
            Выйти
          </button>
        </div>
      </aside>

      <section className="chat-main">
        <header className="chat-top">
          <div className="chat-top-left">
            <button
              type="button"
              className="btn sm icon-btn"
              title={sideOpen ? "Скрыть чаты" : "Показать чаты"}
              aria-label="Toggle sessions"
              onClick={() => setSideOpen((v) => !v)}
            >
              <span className="burger" aria-hidden />
            </button>
            <h1 className="chat-title">{active?.title || "Чат"}</h1>
          </div>

          <div className="chat-top-center">
            <SelectMenu
              className="select-model"
              ariaLabel="Модель"
              value={model}
              placeholder="Нет моделей"
              options={
                models.length
                  ? models.map((m) => ({ value: m, label: m }))
                  : [{ value: "", label: "Нет моделей" }]
              }
              onChange={setModel}
              disabled={!models.length}
            />
            <button
              type="button"
              className={`think-toggle${think ? " on" : ""}`}
              aria-pressed={think}
              onClick={() => setThink((v) => !v)}
            >
              Think
            </button>
            <SelectMenu
              className="select-ctx"
              ariaLabel="Размер контекста"
              value={String(numCtx)}
              options={CTX_OPTIONS}
              onChange={(v) => {
                const next = Number(v);
                setNumCtx(next);
                setCtxUsed(null);
                if (numPredict > next) setNumPredict(next);
              }}
            />
            <SelectMenu
              className="select-max"
              ariaLabel="Лимит токенов ответа"
              value={String(numPredict)}
              options={PREDICT_OPTIONS.filter((o) => Number(o.value) <= numCtx)}
              onChange={(v) => setNumPredict(Number(v))}
            />
            <div
              className={`ctx-meter${ctxUsed != null && ctxUsed / numCtx > 0.85 ? " warn" : ""}${
                ctxUsed != null && ctxUsed / numCtx > 0.95 ? " critical" : ""
              }`}
              title={
                ctxUsed != null
                  ? `Использовано ${ctxUsed.toLocaleString()} из ${numCtx.toLocaleString()} токенов контекста`
                  : "После ответа покажем использование контекста"
              }
            >
              <div className="ctx-meter-bar" aria-hidden>
                <i
                  style={{
                    width: `${Math.min(100, ctxUsed != null ? (ctxUsed / numCtx) * 100 : 0)}%`,
                  }}
                />
              </div>
              <span className="ctx-meter-text">
                {compressing ? (
                  <>сжимаю контекст…</>
                ) : ctxUsed != null ? (
                  <>
                    {formatTokens(ctxUsed)} / {formatTokens(numCtx)}
                    <span className="ctx-left"> · left {formatTokens(Math.max(0, numCtx - ctxUsed))}</span>
                  </>
                ) : (
                  <>ctx {formatTokens(numCtx)}</>
                )}
              </span>
              {active?.summary ? (
                <span className="ctx-compressed" title="Ранняя история сжата в system-промпт">
                  compressed
                </span>
              ) : null}
            </div>
          </div>

          <div className="chat-top-right" ref={menuRef}>
            <button
              type="button"
              className="btn sm icon-btn"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              title="Действия"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="dots" aria-hidden />
            </button>
            {menuOpen ? (
              <div className="chat-menu" role="menu">
                <button type="button" role="menuitem" disabled={empty} onClick={doExport}>
                  Экспорт .md
                </button>
                <button type="button" role="menuitem" disabled={empty} onClick={clearMessages}>
                  Очистить сообщения
                </button>
                <button type="button" role="menuitem" className="danger" onClick={() => deleteChat()}>
                  Удалить чат
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <div className="chat-scroll" ref={scrollRef}>
          <div className="chat-thread" ref={mdRef}>
            {empty ? (
              <div className="chat-empty">
                <div className="empty-mark" aria-hidden>
                  BN
                </div>
                <h2 className="empty-title">Чем помочь?</h2>
                <p className="empty-lead">
                  Локальные модели за WireGuard. Напиши запрос или выбери старт.
                </p>
                <div className="empty-prompts">
                  {PROMPTS.map((p) => (
                    <button
                      key={p.text}
                      type="button"
                      className="prompt-card"
                      onClick={() => void send(p.text)}
                    >
                      <span className="prompt-title">{p.title}</span>
                      <span className="prompt-hint">{p.hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              active!.messages.map((m, i) => {
                const streaming = busy && i === active!.messages.length - 1 && m.role === "assistant";
                const body =
                  m.content || (streaming ? "" : "");
                return (
                  <article key={`${active!.id}_${i}`} className={`msg ${m.role}`}>
                    <div className="msg-meta">
                      <span className="msg-role">{m.role === "user" ? "Вы" : "Модель"}</span>
                      {m.content ? (
                        <button
                          type="button"
                          className="msg-copy"
                          onClick={() => void copyText(m.content)}
                        >
                          Копировать
                        </button>
                      ) : null}
                    </div>
                    {m.thinking ? (
                      <details className="thinking" open={streaming}>
                        <summary>Размышления</summary>
                        <pre>{m.thinking}</pre>
                      </details>
                    ) : null}
                    {m.role === "assistant" ? (
                      <div
                        className={`msg-body${streaming && !body ? " streaming" : ""}`}
                        dangerouslySetInnerHTML={{
                          __html: renderMarkdown(body || (streaming ? "…" : "")),
                        }}
                      />
                    ) : (
                      <div className="msg-body llm-p">{m.content}</div>
                    )}
                    {m.role === "assistant" &&
                    m.truncated &&
                    i === active!.messages.length - 1 &&
                    !busy ? (
                      <div className="trunc-bar">
                        <span>Ответ обрезан по лимиту токенов</span>
                        <button type="button" className="btn sm primary" onClick={() => void continueReply()}>
                          Продолжить
                        </button>
                      </div>
                    ) : null}
                  </article>
                );
              })
            )}
          </div>
        </div>

        <footer className="composer">
          <div className="composer-inner">
            {err ? <p className="err">{err}</p> : null}
            {truncated && !busy ? (
              <div className="trunc-banner">
                <span>Модель остановилась по лимиту — можно дописать ответ.</span>
                <button type="button" className="btn sm primary" onClick={() => void continueReply()}>
                  Продолжить
                </button>
              </div>
            ) : null}
            <div className={`composer-box${busy ? " busy" : ""}`}>
              <textarea
                ref={taRef}
                value={input}
                rows={1}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Напиши сообщение…"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
              />
              <div className="composer-actions">
                {busy ? (
                  <button type="button" className="btn blood send-btn" onClick={stop}>
                    Стоп
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn primary send-btn"
                    disabled={!input.trim()}
                    aria-label="Отправить"
                    onClick={() => void send()}
                  >
                    <span className="send-ico" aria-hidden />
                  </button>
                )}
              </div>
            </div>
            <p className="composer-hint muted tiny">
              {compressing
                ? "Сжимаю раннюю историю в system-контекст…"
                : "Enter — отправить · Shift+Enter — новая строка"}
              {!compressing && model ? ` · ${model}` : ""}
            </p>
          </div>
        </footer>
      </section>
    </div>
  );
}
